import Phaser from "phaser";
import SoundManager from "../game/SoundManager";
import ParticleManager from "../game/ParticleManager";
import MusicManager from "../game/MusicManager";
import { addMuteButton } from "../game/MuteButton";
import { isNarrow } from "../game/view/layout";
import { createStageSim, input, advance, result, workTarget } from "../shared/stage/StageSim";
import { aligned } from "../shared/stage/physics";
import { FONT_SIZE, fontStyle } from "../game/FontScale";
import { fitText } from "../game/view/layout";
import { computeLayout, toScreen } from "../game/stage/stageLayout";
import { paletteFor } from "../game/stage/stagePalette";
import { createWorldView, renderWorld } from "../game/stage/StageWorldView";
import { createActorsView, renderActors } from "../game/stage/StageActorsView";
import { createStageHud, updateStageHud } from "../game/stage/StageHudView";
import { createStageControls } from "../game/stage/StageControls";
import { showStageResult } from "../game/stage/StageResultOverlay";
import StageProgress from "../game/stage/StageProgress";
import DemoConfig from "../game/DemoConfig";
import RunClient from "../game/RunClient";
import XAuth from "../game/XAuth";
import AchievementManager from "../game/AchievementManager";
import { FREE_PLAY } from "../game/FreePlay";
import { stageKind } from "../shared/stage/stageLevels";
import { applyOutcome, continueGame, gameAtStage, woodLogs } from "../shared/stage/arcade";
import { showStageMessage } from "../game/stage/StageResultOverlay";
import { showPaymentGate } from "../game/stage/StagePaymentGate";
import { showContinuePrompt } from "../game/stage/ContinuePrompt";

const INPUT_GRACE_MS = 150; // ignore keys held over from the menu
const KEYS = {
  ArrowLeft: "L",
  KeyA: "L",
  ArrowRight: "R",
  KeyD: "R",
  ArrowUp: "U",
  ArrowDown: "D",
};
const TAPS = { Space: "J", KeyW: "W", KeyC: "C" };

const randomSeed = () => Math.floor(Math.random() * 0x7fffffff);

/**
 * StageScene — "Dams to AGI", the Donkey Kong-style main game.
 * Feeds keys to the deterministic StageSim with timestamps and draws its
 * state. It plays an arcade game (src/shared/stage/arcade.js): lives carry
 * from stage to stage and running out asks for a coin to continue.
 * Signed-in players' games are verified on the server, which issues each
 * stage's seed, energy and lives and replays the submitted inputs. Free-play
 * builds keep the game on this device; the daily stage and challenge links
 * are practice on a shared seed. Outside free-play builds, every mode needs an X sign-in.
 */
export default class StageScene extends Phaser.Scene {
  constructor() {
    super({ key: "StageScene" });
  }

  /**
   * @param {{ level?: number, seed?: number, daily?: boolean, dailyPractice?: boolean, challenge?: boolean, newGame?: boolean, pickStage?: number }} data
   */
  init(data) {
    this.level = data?.level || 1;
    this.seed = data?.seed;
    this.daily = Boolean(data?.daily); // daily challenge: same seed for everyone
    this.dailyPractice = Boolean(data?.dailyPractice);
    this.practice = this.daily || Boolean(data?.challenge); // shared seed, no game, no pot
    this.newGame = Boolean(data?.newGame); // free play: start over from stage 1
    this.pickStage = data?.pickStage; // tester stage select: a new game on this stage
    this.comp = false; // a tester (comp) account: free play everywhere, never ranked
    this.run = null; // { runId } when the server verifies this stage
    this.game = null; // the arcade game this stage belongs to
    this.ready = false;
  }

  create() {
    this._W = this.scale.width;
    this._H = this.scale.height;
    this._CX = this._W / 2;
    this.cameras.main.setBackgroundColor("#000000");

    const kind = stageKind(this.level);
    this.pal = paletteFor(kind);
    this.layout = computeLayout({ W: this._W, H: this._H, narrow: isNarrow(this) });
    this.worldView = createWorldView(this);
    this.actorsView = createActorsView(this);
    this.hud = createStageHud(this, { level: this.level, kind, pal: this.pal });
    ParticleManager.init(this);

    this.inputs = [];
    this.lastT = 0;
    this.done = false;

    this.bindKeyboard();
    if (isNarrow(this)) {
      this.controls = createStageControls(this, { top: this.layout.controlsTop, height: this.layout.controlsH }, (k) => this.send(k));
    }
    addMuteButton(this, { x: this._W - 14, y: this._H - 14 });
    MusicManager.play();
    this.events.on("shutdown", () => ParticleManager.destroy());
    this.cameras.main.fadeIn(250);
    this.prepareRun();
  }

  async prepareRun() {
    const handle = XAuth.getUser()?.handle;
    if (!handle && !FREE_PLAY) {
      this.showSignIn(); // every way into the game needs an X account
      return;
    }
    if (this.practice) {
      this.beginRun(this.seed ?? randomSeed());
    } else if (FREE_PLAY) {
      this.beginLocalGame();
    } else {
      await this.beginVerifiedGame(handle);
    }
  }

  /** Free play: the arcade game lives on this device. */
  beginLocalGame() {
    let game = this.newGame ? StageProgress.reset() : StageProgress.get();
    if (this.pickStage) game = StageProgress.save(gameAtStage(this.pickStage));
    if (game.stage !== this.level) {
      this.scene.restart({ level: game.stage });
      return;
    }
    if (game.over) {
      this.offerContinue(game);
      return;
    }
    this.game = game;
    this.beginRun(randomSeed(), game.bankWh, game.lives);
  }

  /** Verified play: the server resumes or starts the game and issues the stage. */
  async beginVerifiedGame(handle) {
    const started = await RunClient.startGame(this.pickStage);
    if (!this.sys.isActive()) return;
    this.comp = Boolean(started.comp);
    if (!started.ok) {
      if (started.status === 402) {
        this.askForCoin(handle, { subtitle: "TODAY'S FREE GAME IS USED", onPaid: () => this.scene.restart({ level: 1 }) });
      } else {
        this.showProblem(started);
      }
      return;
    }
    if (started.game.stage !== this.level) {
      this.scene.restart({ level: started.game.stage }); // resuming never costs anything
      return;
    }
    const run = await RunClient.start();
    if (!this.sys.isActive()) return;
    if (run.ok) {
      this.run = { runId: run.runId };
      this.game = { ...started.game, lives: run.startLives, bankWh: run.startBankWh };
      this.beginRun(run.seed, run.startBankWh, run.startLives);
    } else if (run.code === "OUT_OF_LIVES") {
      this.offerContinue(run.game);
    } else {
      this.showProblem(run);
    }
  }

  beginRun(seed, startBankWh, startLives) {
    this.seed = seed;
    this.sim = createStageSim({ level: this.level, seed, startBankWh, startLives });
    this.clockStart = performance.now();
    this.readyAt = this.clockStart + INPUT_GRACE_MS;
    this.createHints();
    this.ready = true;
  }

  showSignIn() {
    this.stopControls();
    showStageMessage(this, {
      title: "SIGN IN WITH X",
      lines: ["AN X ACCOUNT IS NEEDED TO PLAY", "YOUR WOOD AND RANK ARE KEPT ON IT"],
      actions: [
        { label: "SIGN IN", onPress: () => XAuth.startAuth() },
        { label: "MENU", onPress: () => this.scene.start("MenuScene") },
      ],
    });
  }

  showProblem(res) {
    this.stopControls();
    const offline = res.status === 0;
    showStageMessage(this, {
      title: offline ? "OFFLINE" : "CAN'T START",
      lines: [offline ? "CONNECT TO PLAY - STAGES ARE VERIFIED ONLINE" : String(res.error || "").toUpperCase()],
      actions: [
        { label: "TRY AGAIN", onPress: () => this.scene.restart({ level: this.level }) },
        { label: "MENU", onPress: () => this.scene.start("MenuScene") },
      ],
    });
  }

  /** $1 for a credit (3 lives); `onPaid` spends it. */
  askForCoin(handle, { subtitle, onPaid }) {
    this.stopControls();
    this.overlayNextY = this.overlayNextY ?? this._H * 0.3;
    showPaymentGate(this, { stage: this.level, handle, subtitle, onPaid });
  }

  /** Out of lives: CONTINUE? countdown, then a credit (or a coin) refills them. */
  offerContinue(game) {
    this.stopControls();
    this.overlayNextY = this.overlayNextY ?? this._H * 0.3;
    const logs = woodLogs(game?.bankWh ?? 0).toLocaleString("en-US");
    showContinuePrompt(this, {
      subtitle: `KEEP STAGE ${this.level} AND YOUR ${logs} LOGS`,
      onContinue: () => this.continueAfterCoin(),
      onGiveUp: () => this.gameOver(),
    });
  }

  async continueAfterCoin() {
    if (FREE_PLAY) {
      StageProgress.save(continueGame(StageProgress.get()));
      this.scene.restart({ level: this.level });
      return;
    }
    const res = await RunClient.continueGame();
    if (!this.sys.isActive()) return;
    if (res.ok) {
      this.scene.restart({ level: res.game.stage });
    } else if (res.status === 402) {
      this.askForCoin(XAuth.getUser()?.handle, {
        subtitle: `CONTINUE ON STAGE ${this.level}`,
        onPaid: () => this.continueAfterCoin(),
      });
    } else {
      this.showProblem(res);
    }
  }

  gameOver() {
    if (FREE_PLAY) StageProgress.reset();
    showStageMessage(this, {
      title: "GAME OVER",
      lines: ["YOUR BEST WOOD STILL COUNTS FOR THE EPOCH"],
      actions: [{ label: "MENU", onPress: () => this.scene.start("MenuScene") }],
    });
  }

  stopControls() {
    if (this.controls) {
      this.controls.destroy();
      this.controls = null;
    }
  }

  /** Goal banner at the start, and a context hint above the woodchuck. */
  createHints() {
    const goal =
      this.sim.kind === "build"
        ? "CLIMB TO THE TOP!\n↑ ON LADDERS · W C W C TO BUILD BROKEN ONES"
        : "DEFEND THE GRID!\nW C W C AT A LEECH TO PRY IT OFF";
    const banner = fitText(
      this.add
        .text(this._CX, this.layout.oy + this.layout.ph * 0.42, goal, fontStyle(FONT_SIZE.BODY, "#FFFFFF", { stroke: "#000000", strokeThickness: 4, align: "center" }))
        .setOrigin(0.5)
        .setDepth(300),
      this._W - 24,
    );
    this.tweens.add({ targets: banner, alpha: 0, delay: 3500, duration: 600, onComplete: () => banner.destroy() });
    this.hint = this.add
      .text(0, 0, "", fontStyle(FONT_SIZE.CAPTION, "#FFD84A", { stroke: "#000000", strokeThickness: 3 }))
      .setOrigin(0.5, 1)
      .setDepth(300);
  }

  /** What the player can do right here: build/pry (W C) or climb (↑ / ↓). */
  updateHint() {
    const s = this.sim;
    const p = s.player;
    let text = "";
    if (p.mode === "ground") {
      const work = workTarget(s);
      if (work) text = work.kind === "leech" ? "W C W C  PRY!" : "W C W C  BUILD!";
      else if (s.ladders.some((l) => l.from === p.tier && l.rungs >= l.need && aligned(p.x, l.x))) text = "↑ CLIMB";
      else if (s.ladders.some((l) => l.from === p.tier && l.rungs < l.need && aligned(p.x, l.x, 24))) text = "BROKEN LADDER";
    }
    this.hint.setText(text);
    if (text) {
      const pos = toScreen(this.layout, p.x, p.y);
      this.hint.setPosition(Math.min(this._W - 60, Math.max(60, pos.x)), pos.y - 24 * this.layout.scale / 1.5);
    }
  }

  bindKeyboard() {
    const kb = this.input.keyboard;
    kb.addCapture(["SPACE", "UP", "DOWN", "LEFT", "RIGHT"]);
    kb.on("keydown", (e) => {
      if (e.repeat) return;
      if (KEYS[e.code]) this.send(`${KEYS[e.code]}+`);
      else if (TAPS[e.code]) this.send(TAPS[e.code]);
    });
    kb.on("keyup", (e) => {
      if (KEYS[e.code]) this.send(`${KEYS[e.code]}-`);
    });
  }

  now() {
    this.lastT = Math.max(this.lastT, Math.round(performance.now() - this.clockStart));
    return this.lastT;
  }

  send(key) {
    if (!this.ready || this.done || performance.now() < this.readyAt) return;
    const t = this.now();
    this.inputs.push([t, key]);
    const out = input(this.sim, key, t);
    this.sim = out.state;
    this.handleEvents(out.events);
  }

  update(time, delta) {
    if (!this.ready || this.done) return;
    const out = advance(this.sim, this.now());
    this.sim = out.state;
    this.handleEvents(out.events);
    if (this.done) return; // the result overlay took over this frame
    renderWorld(this.worldView, this.layout, this.pal, this.sim, delta);
    renderActors(this.actorsView, this.layout, this.pal, this.sim, time);
    updateStageHud(this.hud, this.sim);
    this.updateHint();
    if (this.controls) this.controls.setWorkActive(Boolean(workTarget(this.sim)));
  }

  popAtPlayer(text, color) {
    const p = toScreen(this.layout, this.sim.player.x, this.sim.player.y);
    ParticleManager.emitScorePop(p.x, p.y - 24, text, color);
  }

  handleEvents(events) {
    events.forEach((e) => {
      switch (e.type) {
        case "rung":
          SoundManager.playSqueak();
          break;
        case "built":
          SoundManager.playPatternComplete();
          this.cameras.main.flash(200, 123, 199, 74);
          this.popAtPlayer(`TURBINE +${e.wh} Wh`, "#7BC74A");
          break;
        case "repaired":
          SoundManager.playPatternComplete();
          break;
        case "jumped":
          SoundManager.playCoinDrop();
          this.popAtPlayer(`+${e.wh} Wh`, "#FFD84A");
          break;
        case "smash":
          SoundManager.playComboMilestone(e.wh);
          this.popAtPlayer(`+${e.wh} Wh`, "#FFD84A");
          break;
        case "bite":
          SoundManager.playBossFanfare();
          break;
        case "hit":
          SoundManager.playHazardWarning();
          this.cameras.main.shake(180, 0.008);
          if (e.wh) this.popAtPlayer(`${e.wh} Wh`, "#FF3333");
          break;
        case "leech":
        case "crack":
          SoundManager.playStormWarning();
          break;
        case "pried":
          SoundManager.playCoinDrop();
          this.popAtPlayer(`LEECH OFF +${e.wh} Wh`, "#39FF14");
          break;
        case "complete":
          SoundManager.playLevelComplete();
          this.finish();
          break;
        case "failed":
          SoundManager.playGameOver();
          this.finish();
          break;
        default:
          break;
      }
    });
  }

  async finish() {
    if (this.done) return;
    this.done = true;
    this.stopControls();
    const local = result(this.sim);
    if (this.practice) {
      this.showPracticeResult(local);
      return;
    }
    const outcome = this.run ? await this.verify(local) : this.applyLocally(local);
    if (!this.sys.isActive()) return;
    AchievementManager.checkAfterStage({ ...local, woodLogs: outcome.r.woodLogs });
    this.showGameResult(outcome);
  }

  /** Free play: the same arcade rules, kept on this device. */
  applyLocally(local) {
    const before = StageProgress.get();
    const { game, bonusWh } = applyOutcome(before, local);
    StageProgress.save(game);
    const logs = woodLogs(game.bankWh);
    return {
      r: { ...local, woodLogs: logs, totalWh: game.bankWh, woodDelta: logs - woodLogs(before.bankWh) },
      game,
      bonusWh,
      note: "FREE PLAY - NOT COUNTED",
    };
  }

  /** The buttons after a game stage: next, try again, or the CONTINUE? countdown. */
  gameActions(r, game) {
    const menu = { label: "MENU", onPress: () => this.scene.start("MenuScene") };
    const retry = { label: "TRY AGAIN", color: "#D4884A", onPress: () => this.scene.restart({ level: game ? game.stage : this.level }) };
    if (!game) return [retry, menu];
    if (game.finished) return [menu];
    if (r.status === "complete" && !this.demoCapped()) {
      const label = r.kind === "build" ? "NEXT: DEFEND THE GRID" : "NEXT: BUILD AGAIN";
      return [{ label, color: "#7BC74A", onPress: () => this.scene.restart({ level: game.stage }) }, menu];
    }
    return r.status === "complete" ? [menu] : [retry, menu];
  }

  showGameResult({ r, game, bonusWh, note }) {
    let shownNote = note;
    if (game?.finished) shownNote = "YOU POWERED AGI - THE END";
    else if (this.demoCapped() && r.status === "complete") shownNote = "THE DEMO ENDS HERE - MORE STAGES SOON";
    const over = Boolean(game?.over);
    showStageResult(this, r, { note: shownNote, bonusWh, lives: game ? game.lives : null, actions: over ? [] : this.gameActions(r, game) });
    if (over) this.offerContinue(game);
  }

  /** The public demo stops after stage 1; testers play on. */
  demoCapped() {
    return DemoConfig.isDemoMode() && !this.comp && this.level >= DemoConfig.MAX_LEVEL;
  }

  /** Daily stage / challenge link: a shared seed, nothing carried or counted. */
  showPracticeResult(r) {
    const actions = [];
    if (this.daily) {
      // The scored daily attempt goes straight to its results; practice can go again
      if (this.dailyPractice) {
        actions.push({ label: "PRACTICE AGAIN", color: "#D4884A", onPress: () => this.scene.restart({ level: 1, seed: this.seed, daily: true, dailyPractice: true }) });
      }
      actions.push({ label: "DAILY RESULTS", color: "#7BC74A", onPress: () => this.scene.start("DailyChallengeScene", { stageResult: r, practice: this.dailyPractice }) });
    } else {
      actions.push({ label: "TRY AGAIN", color: "#D4884A", onPress: () => this.scene.restart({ level: this.level, seed: this.seed, challenge: true }) });
      actions.push({ label: "MENU", onPress: () => this.scene.start("MenuScene") });
    }
    showStageResult(this, r, { note: this.daily ? "DAILY STAGE" : "CHALLENGE", actions });
  }

  /** Submit the inputs; the server's replay decides the outcome, the wood and the game. */
  async verify(local) {
    const status = this.add
      .text(this._CX, this._H / 2, "VERIFYING...", fontStyle(FONT_SIZE.HEADING, "#FFD84A", { stroke: "#000000", strokeThickness: 4 }))
      .setOrigin(0.5)
      .setDepth(400);
    const res = await RunClient.finish(this.run.runId, this.inputs);
    status.destroy();
    if (!res.ok) return { r: local, game: null, bonusWh: 0, note: "COULD NOT VERIFY - NOT COUNTED" };
    return {
      r: { ...local, status: res.status, woodLogs: res.woodLogs, totalWh: res.totalWh, woodDelta: res.woodDelta },
      game: res.game,
      bonusWh: res.bonusWh,
      note: res.status === "rejected" ? "YOUR W/C RHYTHM DIDN'T LOOK HUMAN" : "VERIFIED",
    };
  }
}
